import React, { useState, useRef, useEffect, useCallback } from 'react';
import './Roadmap.css';
import roadmapImage from './images/Roadmap.svg';

const Roadmap = () => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  const containerRef = useRef(null);

  // Handle zoom increase
  const increaseZoom = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
    if (!isDragging) {
      setPosition({ x: 0, y: 0 }); // Reset position when zooming
    }
  };

  // Handle zoom decrease
  const decreaseZoom = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
    if (zoomLevel <= 1.25) {
      setPosition({ x: 0, y: 0 }); // Reset position when close to normal zoom
    }
  };

  // Reset zoom and position
  const resetZoom = () => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  // Handle mouse down for dragging
  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  // Handle mouse move for dragging
  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      setPosition({ x: newX, y: newY });
    }
  };

  // Handle mouse up
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle wheel zoom with useCallback
  const handleWheel = useCallback((e) => {
    if (e.ctrlKey) {
      e.preventDefault();
      const delta = e.deltaY * -0.01;
      const newZoom = Math.max(0.5, Math.min(3, zoomLevel + delta));
      setZoomLevel(newZoom);
    }
  }, [zoomLevel]);

  // Handle fullscreen toggle
  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
    resetZoom();
  };

  // Add wheel event listener with proper dependency
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  return (
    <div 
      className="roadmap-container"
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <h1>
        <span className="letter large-c" style={{ color: '#ffffff' }}>C</span>
        <span className="letter" style={{ color: '#f28bf2' }}>l</span>
        <span className="letter" style={{ color: '#f28bf2' }}>o</span>
        <span className="letter" style={{ color: '#f28bf2' }}>u</span>
        <span className="letter" style={{ color: '#f28bf2' }}>d</span>
        <span className="letter" style={{ color: '#ccff90' }}> </span>
        <span className="letter" style={{ color: '#ccff90' }}>C</span>
        <span className="letter" style={{ color: '#ccff90' }}>o</span>
        <span className="letter" style={{ color: '#ccff90' }}>m</span>
        <span className="letter" style={{ color: '#ccff90' }}>p</span>
        <span className="letter" style={{ color: '#ccff90' }}>u</span>
        <span className="letter" style={{ color: '#ccff90' }}>t</span>
        <span className="letter" style={{ color: '#ccff90' }}>i</span>
        <span className="letter" style={{ color: '#ccff90' }}>n</span>
        <span className="letter" style={{ color: '#ccff90' }}>g</span>
      </h1>

      <div className="zoom-buttons">
        <button onClick={increaseZoom} disabled={zoomLevel >= 3}>+</button>
        <button onClick={resetZoom}>Reset</button>
        <button onClick={decreaseZoom} disabled={zoomLevel <= 0.5}>-</button>
        <button onClick={toggleFullScreen}>
          {isFullScreen ? 'Exit Full' : 'Full'}
        </button>
      </div>

      <img
        src={roadmapImage}
        alt="Cloud Computing Roadmap"
        className={`roadmap-image ${isFullScreen ? 'fullscreen' : ''}`}
        style={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${zoomLevel})`,
          cursor: isDragging ? 'grabbing' : (zoomLevel > 1 ? 'grab' : 'default'),
          transition: isDragging ? 'none' : 'transform 0.3s ease'
        }}
        draggable={false}
      />
    </div>
  );
};

export default Roadmap;